import ChatContent from "../../../../../lib/html5-software-library/src/ranj/eng/content/ChatContent";
import ContentTypes from "../../../../../lib/html5-software-library/src/ranj/eng/content/ContentTypes";
import TextContent from "../../../../../lib/html5-software-library/src/ranj/eng/content/TextContent";
import GameClock from "../../../../../lib/html5-software-library/src/ranj/eng/story/framework/clock/GameClock";
import TrackString from "../../../analytics/TrackString";
import AbstractCommand from "../../../core/application/AbstractCommand";
import Config from "../../../core/config/Config";
import NGTVariableManager from "../../base/NGTVariableManager";
import DialogPlayerRegisterEvent from "../../dialog/controller/DialogPlayerRegisterEvent";
import NGTDialogFactory from "../../dialog/NGTDialogFactory";
import NGTDialogPlayer from "../../dialog/NGTDialogPlayer";
import NGTDialogPlayerEvent from "../../dialog/NGTDialogPlayerEvent";
import ScenarioPlayerEvent from "../../scenario/ScenarioPlayerEvent";
import ChatInteractionEvent from "../events/ChatInteractionEvent";
import ChatNpcMessageEvent from "../events/ChatNpcMessageEvent";
import ChatPlayerMessageEvent from "../events/ChatPlayerMessageEvent";
import ChatMessage from "../model/ChatMessage";
import ChatModel from "../model/ChatModel";
import ChatStatus from "../model/ChatStatus";

export default class PlayChatCommand extends AbstractCommand {
    /**
     * @constructor
     * @param {soma.EventDispatcher} dispatcher 
     * @param {ChatModel} chatModel 
     * @param {NGTDialogPlayer} dialogPlayer 
     * @param {NGTDialogFactory} dialogFactory 
     * @param {GameClock} gameClock 
     * @param {NGTVariableManager} variableManager 
     * @param {Config} config 
     */
    constructor(dispatcher, chatModel, dialogPlayer, dialogFactory, gameClock, variableManager, config) {
        super(dispatcher);

        this._chatModel = chatModel;
        this._dialogPlayer = dialogPlayer;
        this._dialogFactory = dialogFactory;
        this._gameClock = gameClock;
        this._variableManager = variableManager;
        this._config = config;
    }

    execute(evt) {
        const data = evt.params.data;

        this._dispatchChatEvent(ChatInteractionEvent.CHAT_STARTED);

        /** @type {ChatContent} */
        const content = data.content;
        const contentPlayer = data.contentPlayer;

        // Setup a dialog player for this chat
        this._dialogPlayer.setContentPlayer(contentPlayer);
        this._dialogPlayer.setContent(content);

        // Retrieve and set dialog for this chat
        const dialogId = content.getChatDialogId();
        const dialog = this._dialogFactory.getDialog(dialogId);
        this._dialogPlayer.setDialog(dialogId, dialog);

        this._npcId = content.getNpcId();
        this._chatModel.ensureConversationForNpcId(this._npcId);
        this._conversation = this._chatModel.getConversationForNpcId(this._npcId);
        this._changeConversationStatus(ChatStatus.ONLINE);
        this._conversation.contentPlayer = this._dialogPlayer;
        this._conversation.addDialogIdToHistory(dialogId);

        this._handleContentCompletedBound = this._handleContentCompleted.bind(this);
        this._dispatcher.addEventListener(ScenarioPlayerEvent.CONTENT_COMPLETED, this._handleContentCompletedBound);

        this._addDialogPlayerListeners();

        this._dialogPlayer.viewContent();
        this._dispatchChatEvent(ChatInteractionEvent.CHAT_STARTED);

        // Analytics: Sends dialog start
        this.trackEvent(TrackString.EVENT_DIALOG_START, TrackString.createDialogParams(dialogId));
    }

    destroy() {
        this._dispatcher.removeEventListener(ScenarioPlayerEvent.CONTENT_COMPLETED, this._handleContentCompletedBound);

        this._removeDialogPlayerListeners();
        this._dialogPlayer.destroy();
    }

    _handleContentCompleted(evt) {
        if (evt.params.data.content !== this._dialogPlayer.getContent()) {
            return; // Completed content wasn't our chat dialog
        }

        const dialogId = evt.params.data.content.getChatDialogId();
        // Deregister the dialog player
        const dialogPlayerRegisterEvent = new DialogPlayerRegisterEvent(DialogPlayerRegisterEvent.DEREGISTER, this._dialogPlayer);
        this.dispatch(dialogPlayerRegisterEvent.type, dialogPlayerRegisterEvent);
        
        // Analytics: Sends dialog end
        this.trackEvent(TrackString.EVENT_DIALOG_END, TrackString.createDialogParams(dialogId));

        this.destroy();
    }

    _addDialogPlayerListeners() {
        this._onDialogContentBound = this._onDialogContent.bind(this);
        this._onDialogOptionsChangedBound = this._onDialogOptionsChanged.bind(this);
        this._onDialogOptionChosenBound = this._onDialogOptionChosen.bind(this);
        this._onDialogEndBound = this._onDialogEnd.bind(this);
        this._onCommandSequenceEndBound = this._onCommandSequenceEnd.bind(this);

        this._dialogPlayer.eventDispatcher.addEventListener(NGTDialogPlayerEvent.DIALOG_CONTENT, this._onDialogContentBound);
        this._dialogPlayer.eventDispatcher.addEventListener(NGTDialogPlayerEvent.DIALOG_OPTIONS_CHANGED, this._onDialogOptionsChangedBound);
        this._dialogPlayer.eventDispatcher.addEventListener(NGTDialogPlayerEvent.DIALOG_OPTION_CHOSEN, this._onDialogOptionChosenBound);
        this._dialogPlayer.eventDispatcher.addEventListener(NGTDialogPlayerEvent.COMMAND_SEQUENCE_END, this._onCommandSequenceEndBound);
        this._dialogPlayer.eventDispatcher.addEventListener(NGTDialogPlayerEvent.DIALOG_END, this._onDialogEndBound);
    }

    _removeDialogPlayerListeners() {
        this._dialogPlayer.eventDispatcher.removeEventListener(NGTDialogPlayerEvent.DIALOG_CONTENT, this._onDialogContentBound);
        this._dialogPlayer.eventDispatcher.removeEventListener(NGTDialogPlayerEvent.DIALOG_OPTIONS_CHANGED, this._onDialogOptionsChangedBound);
        this._dialogPlayer.eventDispatcher.removeEventListener(NGTDialogPlayerEvent.DIALOG_OPTION_CHOSEN, this._onDialogOptionChosenBound);
        this._dialogPlayer.eventDispatcher.removeEventListener(NGTDialogPlayerEvent.COMMAND_SEQUENCE_END, this._onCommandSequenceEndBound);
        this._dialogPlayer.eventDispatcher.removeEventListener(NGTDialogPlayerEvent.DIALOG_END, this._onDialogEndBound);
    }

    _onDialogContent(evt) {
        if (evt.params.contentPlayer !== this._dialogPlayer) {
            return;
        }

        const data = evt.params.data;
        const dialogContentPackage = data.data;
        if (dialogContentPackage !== null) {
            const characterId = dialogContentPackage.characterID;
            const content = dialogContentPackage.content;
            switch (content.getType()) {
                case ContentTypes.TYPE_TEXT:
                    this._changeConversationStatus(ChatStatus.TYPING);
                    setTimeout(() => {
                        this._handleTextContent(content, characterId);
                        this._dialogPlayer.contentCompleted(content);
                    }, this._config.getValue(`chat.npcTypeDuration`) || 1500);
                    break;
            }
        }
    }

    _onDialogOptionsChanged(evt) {
        if (evt.params.contentPlayer !== this._dialogPlayer) {
            return;
        }

        // Saving the options in done when the command sequence has ended
    }

    _onDialogOptionChosen(evt) {
        if (evt.params.contentPlayer !== this._dialogPlayer) {
            return;
        }

        this._conversation.clearOptions();

        const option = evt.params.data.option;
        const contentPackage = option.getData().getData();
        /** @type {TextContent} */
        const optionContent = contentPackage.content;
        const message = new ChatMessage(optionContent, true, this._getCurrentTime(), null, true);
        this._conversation.addMessage(message);

        const playerMessageEvt = new ChatPlayerMessageEvent(ChatPlayerMessageEvent.SENT, message);
        this._dispatchChatEvent(playerMessageEvt.type, { data: playerMessageEvt });
    }

    _onCommandSequenceEnd(evt) {
        if (evt.params.contentPlayer !== this._dialogPlayer) {
            return;
        }

        this._changeConversationStatus(ChatStatus.ONLINE);

        // All options have been unlocked as we are at the end of the command sequence
        const options = this._dialogPlayer.getCurrentOptions();

        if (options.length > 0) {
            this._conversation.addOptions(options);
            this._dispatchChatEvent(ChatInteractionEvent.NEW_PLAYER_OPTIONS_AVAILABLE);
        }
    }

    _onDialogEnd(evt) {
        if (evt.params.contentPlayer !== this._dialogPlayer) {
            return;
        }

        this._changeConversationStatus(ChatStatus.OFFLINE);
        this._conversation.contentPlayer = null;
        this._dispatchChatEvent(ChatInteractionEvent.CHAT_ENDED);
    }

    _getCurrentTime() {
        // Can be changed to what time context the game uses.
        // For example, the game clock:
        // return this._gameClock.getCurrentTime();
        // Or a variable set in the story:
        return this._variableManager.solve('storyTime') || 0;
    }

    /**
     * Handles incoming text content
     * @param {TextContent} content 
     * @param {string} characterId 
     */
    _handleTextContent(content, characterId) {
        const message = new ChatMessage(content, false, this._getCurrentTime(), characterId, false);
        this._conversation.addMessage(message);

        const npcMessageEvt = new ChatNpcMessageEvent(ChatNpcMessageEvent.SENT, message);
        this._dispatchChatEvent(npcMessageEvt.type, { data: npcMessageEvt });
    }

    _changeConversationStatus(value) {
        this._conversation.status = value;
        this._dispatchChatEvent(ChatInteractionEvent.CHAT_STATUS_CHANGED);
    }

    _dispatchChatEvent(type, dataObj = {}) {
        if (!dataObj.data) {
            dataObj.data = {};
        }
        dataObj.data.conversation = this._conversation;
        dataObj.data.contentPlayer = this._dialogPlayer;

        this.dispatch(type, dataObj);
    }
}